import { Medallion, Pill } from '@/components/bedrock';
import { Reveal } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

/**
 * One of the four headline blocks: a claim on one side, the thing happening on
 * the other.
 *
 * ## Why the sides alternate
 *
 * Four identical left-text/right-picture blocks read as a template, and a
 * reader who has recognised the template stops reading the third one. Swapping
 * the order on the even blocks costs nothing and keeps the eye moving — and it
 * is done with `order` on the CHILDREN rather than by reversing the grid,
 * because `flex-row-reverse` and friends are physical directions: under Arabic
 * and Hebrew the whole page is already mirrored, and a second reversal on top
 * of it puts the picture back where it started.
 *
 * ## Why the claim carries three bullets and not a paragraph
 *
 * The block above it (the section header) has already made the argument in
 * prose. What a reader wants here is the specifics they would need to argue
 * for this internally — and three scannable facts are re-tellable in a way a
 * paragraph is not.
 */
export interface PillarProps {
  /** i18n namespace under `features.pillar.` — also the React key + anchor id. */
  readonly id: string;
  readonly icon: LucideIcon;
  readonly hue: MedallionHue;
  /** Number of `features.pillar.<id>.point.N` bullets to render. */
  readonly pointCount: number;
  /** Zero-based; even blocks put the visual second, odd blocks put it first. */
  readonly index: number;
  readonly visual: ReactNode;
}

export function Pillar({ id, icon, hue, pointCount, index, visual }: PillarProps): ReactElement {
  const { t } = useT();
  const visualFirst = index % 2 === 1;

  return (
    <Reveal
      as="section"
      aria-labelledby={`pillar-${id}`}
      className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14"
    >
      <div className={visualFirst ? 'lg:order-2' : ''}>
        <div className="flex items-center gap-3">
          <Medallion icon={icon} hue={hue} />
          <Pill>{t(`features.pillar.${id}.tag`)}</Pill>
        </div>

        <h3
          id={`pillar-${id}`}
          className="mt-5 text-balance text-[clamp(1.6rem,2.9vw,2.15rem)] font-semibold leading-[1.1] tracking-[-0.038em] text-[color:var(--bd-ink)]"
        >
          {t(`features.pillar.${id}.title`)}
        </h3>

        <p className="mt-4 max-w-[54ch] text-[1.04rem] leading-[1.68] text-[color:var(--bd-body)]">
          {t(`features.pillar.${id}.body`)}
        </p>

        <ul className="mt-6 flex flex-col gap-3">
          {Array.from({ length: pointCount }, (_, i) => (
            <li
              key={i}
              className="flex gap-3 text-[0.95rem] leading-[1.6] text-[color:var(--bd-muted)]"
            >
              <span
                aria-hidden="true"
                className="mt-[0.55em] size-[5px] flex-none rounded-full"
                style={{ background: 'var(--bd-gradient)' }}
              />
              <span>{t(`features.pillar.${id}.point.${i + 1}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={visualFirst ? 'lg:order-1' : ''}>{visual}</div>
    </Reveal>
  );
}
